/**
 * @engine/vfx - Update Context Nodes
 *
 * Nodes for updating particles each frame.
 */

import type { VFXNodeDefinition } from '../../types';
import { dataPort, flowPort } from '../../types';

/**
 * Update context node definitions.
 */

export const MISC_UPDATE_NODES: readonly VFXNodeDefinition[] = [
  // ---- Flipbook Animation ----
  {
    id: 'vfx.update.flipbook',
    name: 'Flipbook Animation',
    category: 'attribute',
    contexts: ['update'],
    description: 'Animate flipbook frame over time',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('frameCount', 'Frame Count', 'int', { defaultValue: 16, range: { min: 1, max: 256 } }),
      dataPort('fps', 'FPS', 'float', { defaultValue: 30, range: { min: 1, max: 120 } }),
      dataPort('loop', 'Loop', 'bool', { defaultValue: true }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let flipFrame = particle.lifetime * fps;
        if (loop) {
          particle.spriteFrame = flipFrame % float(frameCount);
        } else {
          particle.spriteFrame = min(flipFrame, float(frameCount - 1));
        }
      `,
    },
  },

  // ---- Orbit ----
  {
    id: 'vfx.update.orbit',
    name: 'Orbit',
    category: 'force',
    contexts: ['update'],
    description: 'Orbital motion around a point',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('center', 'Center', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('axis', 'Axis', 'vec3', { defaultValue: { x: 0, y: 1, z: 0 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 2, range: { min: -20, max: 20 } }),
      dataPort('radius', 'Radius', 'float', { defaultValue: 0, range: { min: 0, max: 100 } }),
      dataPort('radiusVariance', 'Radius Variance', 'float', { defaultValue: 0, range: { min: 0, max: 50 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let axisNorm = normalize(axis);
        let toParticle = particle.position - center;

        // Project onto rotation plane
        let projOnAxis = dot(toParticle, axisNorm) * axisNorm;
        let radialVec = toParticle - projOnAxis;
        let currentRadius = length(radialVec);

        if (currentRadius > 0.001) {
          let radialNorm = radialVec / currentRadius;
          let tangent = cross(axisNorm, radialNorm);

          // Apply orbital velocity
          let angularSpeed = speed;
          particle.velocity += tangent * angularSpeed * deltaTime;

          // Optionally constrain to radius
          if (radius > 0.0) {
            let targetRadius = radius + (hash(particle.seed + 100u) * 2.0 - 1.0) * radiusVariance;
            let radialForce = (targetRadius - currentRadius) * 2.0;
            particle.velocity += radialNorm * radialForce * deltaTime;
          }
        }
      `,
    },
  },

  // ---- Limit Velocity ----
  {
    id: 'vfx.update.limit_velocity',
    name: 'Limit Velocity',
    category: 'force',
    contexts: ['update'],
    description: 'Clamp particle velocity to min/max range',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('minSpeed', 'Min Speed', 'float', { defaultValue: 0, range: { min: 0, max: 100 } }),
      dataPort('maxSpeed', 'Max Speed', 'float', { defaultValue: 50, range: { min: 0, max: 500 } }),
      dataPort('damping', 'Damping', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let speed = length(particle.velocity);
        if (speed > 0.001) {
          var newSpeed = speed;

          // Apply damping if over max
          if (speed > maxSpeed) {
            newSpeed = lerp(speed, maxSpeed, damping);
          }
          // Boost if under min
          else if (speed < minSpeed && minSpeed > 0.0) {
            newSpeed = lerp(speed, minSpeed, damping);
          }

          particle.velocity = particle.velocity / speed * newSpeed;
        }
      `,
    },
  },

  // ---- Conform to Sphere ----
  {
    id: 'vfx.update.conform_to_sphere',
    name: 'Conform to Sphere',
    category: 'force',
    contexts: ['update'],
    description: 'Attract particles to sphere surface',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('center', 'Center', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('radius', 'Radius', 'float', { defaultValue: 5, range: { min: 0, max: 100 } }),
      dataPort('strength', 'Strength', 'float', { defaultValue: 5, range: { min: 0, max: 50 } }),
      dataPort('mode', 'Mode', 'int', { defaultValue: 0, options: ['Surface', 'Inside', 'Outside'] }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let toParticle = particle.position - center;
        let dist = length(toParticle);

        if (dist > 0.001) {
          let dir = toParticle / dist;
          var force = 0.0;

          // Mode 0: Attract to surface
          if (mode == 0) {
            force = (radius - dist) * strength;
          }
          // Mode 1: Keep inside
          else if (mode == 1) {
            if (dist > radius) {
              force = (radius - dist) * strength;
            }
          }
          // Mode 2: Keep outside
          else if (mode == 2) {
            if (dist < radius) {
              force = (radius - dist) * strength;
            }
          }

          particle.velocity += dir * force * deltaTime;
        }
      `,
    },
  },

  // ---- Sphere Attractor ----
  {
    id: 'vfx.update.sphere_attractor',
    name: 'Sphere Attractor',
    category: 'force',
    contexts: ['update'],
    description: 'Attract and stick particles to sphere surface with smooth falloff',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('center', 'Center', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('radius', 'Radius', 'float', { defaultValue: 5, range: { min: 0, max: 100 } }),
      dataPort('attractionSpeed', 'Attraction Speed', 'float', { defaultValue: 5, range: { min: 0, max: 50 } }),
      dataPort('attractionForce', 'Attraction Force', 'float', { defaultValue: 20, range: { min: 0, max: 100 } }),
      dataPort('stickDistance', 'Stick Distance', 'float', { defaultValue: 0.1, range: { min: 0, max: 5 } }),
      dataPort('stickForce', 'Stick Force', 'float', { defaultValue: 50, range: { min: 0, max: 200 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let offset = particle.position - center;
        let dist = length(offset);
        let n = offset / max(dist, 0.0001);

        // Distance from sphere surface (positive = outside, negative = inside)
        let surfaceDist = dist - radius;
        let absSurfaceDist = abs(surfaceDist);

        // Radial velocity component
        let radialVel = dot(particle.velocity, n);

        // Smooth transition: full stick when close, full attraction when far
        let t = saturate(absSurfaceDist / max(stickDistance, 0.0001));
        let force = lerp(stickForce, attractionForce, t * t);

        // Target radial velocity pulls toward surface
        let targetVel = -sign(surfaceDist) * attractionSpeed * t;

        // Spring-damper force toward target velocity
        // currently mass is not implemented, so treat as 1
        let accel = (targetVel - radialVel) * force / 1.0;
        particle.velocity += n * accel * deltaTime;
      `,
    },
  },
];
